/**
 * SSE streaming client for `POST /api/chat/conversations/:id/messages`.
 *
 * Mirrors the assistant-stream.ts contract (user_message / delta / done /
 * error events) but is parametrized on conversation id and exposes a
 * "sources" array on the done event so the UI can render which document
 * chunks were used by RAG.
 */

import { getImpersonationHeaders } from "./impersonation";

export interface ChatStreamMessage {
  id: string;
  role: string;
  content: string;
  meta: Record<string, unknown> | null;
  createdAt: string;
}

export interface ChatStreamSource {
  fileId: string;
  fileName: string;
  chunkIndex: number;
}

export interface ChatStreamDoneEvent {
  assistantMessage: ChatStreamMessage;
  capUsed: number;
  capRemaining: number;
  capTotal: number;
  capReached: boolean;
}

export interface ChatStreamCallbacks {
  onUserMessage: (msg: ChatStreamMessage) => void;
  onDelta: (text: string) => void;
  onDone: (payload: ChatStreamDoneEvent) => void;
  onError: (err: Error) => void;
}

export async function sendChatMessageStream(
  conversationId: string,
  content: string,
  callbacks: ChatStreamCallbacks,
  signal?: AbortSignal,
): Promise<void> {
  let response: Response;
  try {
    response = await fetch(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...getImpersonationHeaders(),
        },
        body: JSON.stringify({ content }),
        signal,
        credentials: "same-origin",
      },
    );
  } catch (err) {
    callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!response.ok || !response.body) {
    callbacks.onError(new Error(`HTTP ${response.status}`));
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let receivedDone = false;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIdx;
      while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const evt = parseSseEvent(rawEvent);
        if (!evt) continue;
        if (evt.event === "user_message") {
          try {
            callbacks.onUserMessage(JSON.parse(evt.data) as ChatStreamMessage);
          } catch {
            /* ignore */
          }
        } else if (evt.event === "delta") {
          try {
            const payload = JSON.parse(evt.data) as { text?: string };
            if (typeof payload.text === "string" && payload.text.length > 0) {
              callbacks.onDelta(payload.text);
            }
          } catch {
            /* ignore */
          }
        } else if (evt.event === "done") {
          try {
            callbacks.onDone(JSON.parse(evt.data) as ChatStreamDoneEvent);
            receivedDone = true;
          } catch (err) {
            callbacks.onError(
              err instanceof Error ? err : new Error("invalid done event"),
            );
          }
        } else if (evt.event === "error") {
          try {
            const payload = JSON.parse(evt.data) as { error?: string };
            callbacks.onError(new Error(payload.error ?? "stream_failed"));
          } catch {
            callbacks.onError(new Error("stream_failed"));
          }
        }
      }
    }
  } catch (err) {
    callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    return;
  }

  if (!receivedDone) {
    callbacks.onError(new Error("stream_truncated"));
  }
}

interface ParsedSseEvent {
  event: string;
  data: string;
}

function parseSseEvent(raw: string): ParsedSseEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    const value =
      colon + 1 < line.length && line[colon + 1] === " "
        ? line.slice(colon + 2)
        : line.slice(colon + 1);
    if (field === "event") event = value;
    else if (field === "data") dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}
